"use client";

import React, { useState } from "react";
import Image from "next/image";
import { UNTITLED } from "@/utils/dataCleaning";
import { themeColors } from "@/utils/themeColors";
import { Flex, FlexColumn } from "@/components/Flex";
import ViewMoreButton from "@/components/CircularButton/styled/ViewMoreButton";
import { ASSETS_URL } from "@/config/deployConstants";
import FolderCard from "@/components/cards/FolderCard";
import CircularButton from "@/components/CircularButton";
import UserContentCard from "@/components/cards/UserContentCard";
import { ItemType, type FlashcardSet, type Folder, type Note, type UserDetails } from "@knowt/syncing/graphql/schema";
import br from "@/styles/breakpoints.module.css";
import clsx from "clsx";
import RepeatAutoFillGrid from "@/components/styled/div/RepeatAutoFillGrid";
import { UserContentSortBy, UserContentSortSettings } from "@/features/UserContentManagement/types";
import { filterUserContentItems } from "@/features/UserContentManagement/utils";
import SortButton from "@/features/UserContentManagement/components/ControlSections/SortButton";
import { SortOrder } from "@/components/styled/table/types";

const ProfileNoContentEmptyState = ({ isOwner = false }) => {
    return (
        <Flex
            className={clsx(br.smDownColumn, br.smDownTextAlignCenter)}
            style={{
                margin: "10rem auto",
                gap: "1rem",
                maxWidth: "75rem",
                alignItems: "center",
                color: themeColors.neutralBlack,
            }}>
            <Image
                src={`${ASSETS_URL}/images/padlock.png`}
                alt={"No notes image"}
                width={160}
                height={160}
                style={{ objectFit: "contain" }}
            />
            <FlexColumn style={{ gap: "1rem" }}>
                <span
                    className="knowtFont600"
                    style={{ fontWeight: "600", fontSize: "2.3rem" }}
                    data-testid="auto-id-bc19e575-32f5-48d3-97cc-535aeaa2f19d">
                    The notes/flashcard sets are private!
                </span>
                <span
                    style={{ fontSize: "1.5rem", fontWeight: "bold" }}
                    data-testid="auto-id-6b79bbaa-86f8-4721-b2d1-af2969778ab9">
                    {isOwner
                        ? "We only show the items you have set to be public on your profile screen. It looks like everything you have is private."
                        : "We only show the items they have set to be public on their profile screen."}
                </span>
            </FlexColumn>
        </Flex>
    );
};

const ProfileNoFilteredContentEmptyState = () => {
    return (
        <Flex
            className={clsx(br.smDownColumn, br.smDownTextAlignCenter)}
            style={{
                margin: "10rem auto",
                gap: "2rem",
                maxWidth: "75rem",
                alignItems: "center",
                color: themeColors.neutralBlack,
            }}>
            <CircularButton style={{ backgroundColor: "#F6EEE2" }} radius={"13rem"}>
                <Image src={`${ASSETS_URL}/images/search-icon.png`} alt={"search"} width={"60"} height={"60"} />
            </CircularButton>
            <FlexColumn style={{ gap: "1rem" }}>
                <span
                    className="knowtFont600"
                    style={{ fontWeight: "600", fontSize: "2.3rem" }}
                    data-testid="auto-id-cc4b924c-cd21-40d7-a65c-bfb3a688fa86">
                    No matching notes/flashcard sets!
                </span>
                <span
                    style={{ fontSize: "1.5rem", fontWeight: "bold" }}
                    data-testid="auto-id-77193c70-dfa5-4347-90a0-90e783c31e31">
                    Try changing your filters to see more content.
                </span>
            </FlexColumn>
        </Flex>
    );
};

export const sortItems = (items: (Note | FlashcardSet)[], sort: UserContentSortSettings) => {
    const isAscending = sort.direction === SortOrder.ASC;

    return items.sort((card1, card2) => {
        const { created: created1, updated: updated1 } = card1 as { created: number; updated: number };
        const { created: created2, updated: updated2 } = card2 as { created: number; updated: number };
        const title1 = card1?.title?.length ? card1.title : UNTITLED;
        const title2 = card2?.title?.length ? card2.title : UNTITLED;

        switch (sort.by) {
            case UserContentSortBy.CREATED:
                return (created1 - created2) * (isAscending ? 1 : -1);
            case UserContentSortBy.NAME:
                return title1.localeCompare(title2, undefined, { numeric: true }) * (isAscending ? 1 : -1);
            default:
                return (updated1 - updated2) * (isAscending ? 1 : -1);
        }
    });
};

const ProfileUserContentItems = ({
    items,
    folders,
    ownerDetails,
    isOwner,
}: {
    items: (Note | FlashcardSet)[];
    folders: Folder[];
    ownerDetails: UserDetails;
    isOwner: boolean;
}) => {
    const [sort, setSort] = useState<UserContentSortSettings>({
        by: UserContentSortBy.LAST_UPDATED,
        direction: SortOrder.DESC,
    });

    const [displayItemCount, setDisplayItemCount] = useState(10);

    const sortedAndFilteredNotesAndFlashcardSets = sortItems(
        filterUserContentItems(items, [ItemType.NOTE, ItemType.FLASHCARDSET]).filter(({ trash }) => !trash) as (
            | Note
            | FlashcardSet
        )[],
        sort
    );

    const isMoreToSee = sortedAndFilteredNotesAndFlashcardSets.length > displayItemCount;

    const handleViewMore = () => {
        setDisplayItemCount(displayItemCount + 10);
    };

    const showNoContentEmptyState = !folders.length && !items.length;

    const showNoFilteredContentEmptyState = !folders.length && !sortedAndFilteredNotesAndFlashcardSets.length;

    if (showNoContentEmptyState) return <ProfileNoContentEmptyState isOwner={isOwner} />;

    return (
        <React.Fragment>
            <Flex style={{ justifyContent: "flex-end", marginBottom: "2.4rem" }}>
                <SortButton
                    onChange={({ by, direction }) => setSort({ by, direction })}
                    by={sort.by}
                    direction={sort.direction}
                />
            </Flex>

            {folders.length ? (
                <FlexColumn style={{ gap: "1.5rem", marginBottom: "2.4rem" }}>
                    <RepeatAutoFillGrid itemMinWidth="27rem" style={{ gap: "3rem 2.5rem" }}>
                        {folders.map(folder => {
                            return <FolderCard key={folder.folderId} folder={folder} />;
                        })}
                    </RepeatAutoFillGrid>
                </FlexColumn>
            ) : null}
            {showNoFilteredContentEmptyState && <ProfileNoFilteredContentEmptyState />}

            <FlexColumn style={{ gap: "1.5rem", minHeight: "25rem" }}>
                {!!sortedAndFilteredNotesAndFlashcardSets.length && (
                    <RepeatAutoFillGrid itemMinWidth="27rem" style={{ minHeight: "30rem", gap: "3rem 2.5rem" }}>
                        {sortedAndFilteredNotesAndFlashcardSets
                            .slice(0, isMoreToSee ? displayItemCount : sortedAndFilteredNotesAndFlashcardSets.length)
                            .map((item, idx) => {
                                const isNote = !("flashcardSetId" in item);
                                const itemId = isNote ? item.noteId : item.flashcardSetId;
                                return (
                                    <UserContentCard
                                        key={"savedItem" + itemId + idx}
                                        {...(isNote ? { note: item as Note } : { flashcardSet: item as FlashcardSet })}
                                        ownerDetails={ownerDetails}
                                    />
                                );
                            })}
                    </RepeatAutoFillGrid>
                )}
            </FlexColumn>
            {isMoreToSee && (
                <Flex style={{ justifyContent: "flex-end", marginTop: "3rem" }}>
                    <ViewMoreButton onClick={() => handleViewMore()} />
                </Flex>
            )}
        </React.Fragment>
    );
};

export default ProfileUserContentItems;
